import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { isZendeskMessagingUnread } from "@src/appV2/lib/ZendeskMessaging/utils";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useIsInteractiveSupportEnabled } from "@src/appV2/redesign/Support/hooks/useIsInteractiveSupportEnabled";
import { GUIDED_SUPPORT_FULL_PATH } from "@src/appV2/redesign/Support/paths";
import { Badge, ListItem, Title } from "@src/appV2/redesign/system/ui-components";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { USER_EVENTS } from "@src/constants";
import { useHistory } from "react-router-dom";

import { TERMS_OF_SERVICE_URL } from "../../constants/urls";
import { AccountListItem } from "../components/AccountListItem";
import { useHasUpcomingOrInProgressShift } from "../hooks/useHasUpcomingOrInProgressShift";
import { useIsTimeclockInfoWhenContactingSupportEnabled } from "../hooks/useIsTimeclockInfoWhenContactingSupportEnabled";
import { logAccountPageMenuItemTapped } from "../utils";

interface AccountsPageSupportSectionProps {
  onGetHelpClick: () => void;
  onContactSupportClick: () => void;
}

/**
 * SupportSection Component
 *
 * Displays support-related menu items on the Account page.
 *
 * "Get Help" Button Behavior:
 * The button shows different flows based on feature flag and shift status:
 * - If feature flag is enabled AND worker has an in-progress shift:
 *   Opens GetHelpDuringShiftBottomSheet (intervention flow)
 * - Otherwise: Navigates to legacy /needhelp page
 *
 * Feature Flag: The intervention flow is gated behind the LaunchDarkly feature flag
 * "2025-18-orbit-timeclock-info-when-contacting-support". Both the feature flag AND
 * an in-progress shift must be true for the intervention to appear.
 */
export function AccountsPageSupportSection(props: AccountsPageSupportSectionProps) {
  const { onGetHelpClick, onContactSupportClick } = props;
  const { isEmbedded } = useEmbeddedMode();
  const history = useHistory();

  // Check if interactive support flow is enabled (feature flag)
  const isInteractiveSupportEnabled = useIsInteractiveSupportEnabled();
  // Check if worker has an in-progress shift to determine which flow to show
  const hasUpcomingOrInProgressShift = useHasUpcomingOrInProgressShift();
  // Check if the feature flag is enabled for this user
  const isTimeclockInfoEnabled = useIsTimeclockInfoWhenContactingSupportEnabled();
  const { openArticle } = useZendeskArticle();
  const { helpCenterWorkerSupportLink } = useSupportLinks();

  return (
    <>
      <Title semibold component="h4" sx={(theme) => ({ paddingTop: theme.spacing(6) })}>
        Support
      </Title>
      {!isEmbedded && (
        <AccountListItem
          primaryText="Terms of Service"
          iconType="info"
          onClick={async () => {
            logAccountPageMenuItemTapped(APP_V2_USER_EVENTS.OPENED_TERMS_OF_SERVICE);
            await openInBrowser({ url: TERMS_OF_SERVICE_URL });
          }}
        />
      )}

      <ListItem
        secondaryAction={
          isZendeskMessagingUnread() ? (
            // showZero is required because the custom Badge always passes count
            // as MUI's badgeContent, which would otherwise hide the badge when 0.
            <Badge showZero color="error" variant="dot" count={0} />
          ) : undefined
        }
      >
        <AccountListItem
          primaryText="Contact Support"
          iconType="text-bubble"
          onClick={async () => {
            logAccountPageMenuItemTapped(APP_V2_USER_EVENTS.OPENED_CONTACT_SUPPORT);
            // If interactive support is enabled, navigate to guided support
            if (isInteractiveSupportEnabled) {
              history.push(GUIDED_SUPPORT_FULL_PATH);
              return;
            }
            // Only show intervention if feature flag is enabled AND shift is in progress
            if (isTimeclockInfoEnabled && hasUpcomingOrInProgressShift) {
              onContactSupportClick();
            } else {
              await openZendeskMessaging();
            }
          }}
        />
      </ListItem>

      {!isEmbedded && (
        <AccountListItem
          fullWidthDivider
          primaryText="Get Help"
          iconType="info"
          secondaryText="View help articles"
          onClick={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            logAccountPageMenuItemTapped(USER_EVENTS.OPENED_HELP_CENTER);
            // Show intervention bottom sheet only if feature flag is enabled AND shift is in progress
            // Otherwise, use legacy flow (navigate to /needhelp)
            try {
              if (isTimeclockInfoEnabled && hasUpcomingOrInProgressShift) {
                onGetHelpClick();
              } else {
                await openArticle(helpCenterWorkerSupportLink);
              }
            } catch (error) {
              logError(APP_V2_APP_EVENTS.FAILED_TO_OPEN_HELP_CENTER_ARTICLE, { error });
            }
          }}
        />
      )}
    </>
  );
}
